// src/app/tokens.js
//
// Design tokens extracted from the legacy monolith (Ripple Workplace App.html).
// `T` = colors, `STAGES` = stage label/color metadata, `HEALTH` = health
// label/color metadata, `REGIONS` = enum.
//
// This is the *partial* extraction needed for the Locations slice. Other
// feature slices will append their token blocks (lease status, project
// status, candidate status, etc.) in subsequent PRs. Source of truth for
// existing keys: the monolith. Do not invent new colors here without an
// audit pass.

const FW_REGULAR = 400;
const TOOLTIP_FONT_SIZE = 11;
const TOOLTIP_LINE_HEIGHT = 1.5;
const BORDER_DARK_NEUTRAL = "#cbd5e1";

export const T = {
  // DEV impersonation only; never use this family for ordinary status UI.
  impersonationBg: "#ffd166",
  impersonationText: "#352300",
  impersonationBorder: "#b47800",
  // Backgrounds & UI
  bg:         "#eef2f7",
  card:       "#ffffff",
  border:           "#e2e8f0",
  borderDark:       BORDER_DARK_NEUTRAL,
  collectionCount:  BORDER_DARK_NEUTRAL, // Count-only ink, one neutral step lighter than T.light.
  borderCompact:    "#dce5ef",  // compact KPI tile border — muted blue-gray, cooler/lighter than T.border
  switchOff:  "#cfd8e3",  // on/off switch track — off-state grey-blue fill (CompactDensitySwitch)
  adminExplainerBg: "#eef3fc",
  adminExplainerBorder: "#d7e2f5",
  adminExplainerFg: "#183153",
  adminExplainerLabel: "#49668f",
  // Projects home hub — approved weekly-band and segmented-lens palette.
  projectsHubLive: "#0e7490",
  projectsHubDetail: "#155e75",
  projectsHubNavy: "#1E3A5F",
  // Reference/peek controls only; distinct from action blue and RAG status.
  projectsReferenceGold: "#b8860b",
  projectsReferenceText: "#806008",
  projectsReferenceBg: "#fbf3dd",
  projectsReferenceBorder: "#ead9a8",
  projectsReferenceHover: "#f7e9c4",
  projectsHubLine: "#e6e8ec",
  projectsWeeklySurface: "#ecfeff", // Project detail working-updates header wash
  projectsWeeklyBorder: "#bae6fd",  // Project detail working-updates zone border
  projectsHubManageLink: "#2f6feb", // Preserve the existing v2 accent on plain Manage links.
  // Projects landing (task 8674) - scoped to the Portfolio landing only.
  projectsStripBg: "#eef2ff",       // compact current-week strip wash
  projectsStripBorder: "#c7d2fe",   // compact current-week strip border
  projectsStripText: "#4f46e5",     // compact current-week strip text
  projectsPeekShadow: "0 2px 10px rgba(16,24,40,.07)", // lifted open row + briefing peek
  projectsPeekHair: "#e4e7ec",      // 1px hairline around lifted row + peek
  projectsRowDim: 0.4,              // opacity of non-open rows while a peek is open
  projectsPhaseDone: "#1E3A5F",     // prior phases (navy)
  projectsPhaseCurrent: "#2563eb",  // current phase (blue)
  projectsPhaseFuture: "#d0d5dd",   // future phases (grey)
  projectsTypeBuildFg: "#1d4ed8", projectsTypeBuildBg: "#e8efff",
  projectsTypeRenoFg: "#475467",  projectsTypeRenoBg: "#eef1f5",
  projectsTypeExitFg: "#b42318",  projectsTypeExitBg: "#fdecea",
  projectsPostedDot: "#16a34a", projectsPartialDot: "#d97706", projectsNotYetDot: "#98a2b3",
  projectsMenuShadow: "0 8px 24px rgba(16,36,58,0.16)",
  // Projects Travel (mockup-derived purpose / holiday / signal palette).
  travelSiteBg: "#f7d4e3", travelSiteFg: "#8a1f4f",
  travelBoxBg: "#cdeee0", travelBoxFg: "#13603f",
  travelSupportBg: "#dbe8fa", travelSupportFg: "#1d4f9a",
  travelEventBg: "#f3f0fa", travelEventFg: "#5b3fa0", travelEventBorder: "#e4dcf5",
  travelOtherBg: "#eef1f5", travelOtherFg: "#475467",
  travelHolidayA: "#f3f4f6", travelHolidayB: "#eceef1", travelHolidayFg: "#475569", travelHolidayBorder: "#dfe3e8",
  travelGapBorder: "#c9861a",
  travelAmberBg: "#fff8e8", travelAmberBorder: "#f1dfb3", travelAmberFg: "#8a5a0c",
  travelRedBg: "#fff4f4", travelRedBorder: "#f5c2c2", travelRedFg: "#9b1c1c",
  travelDowBg: "#f6f8fb", travelOutBg: "#fafbfd", travelHair: "#eef2f6", travelSegBg: "#dfe6ee",
  travelFocusRing: "rgba(37,99,235,.35)", travelSegShadow: "0 1px 2px rgba(15,27,45,.08)",
  // Exact v2 aliases for shared list presentation; do not change legacy ink.
  listInk: "var(--v2-dark)",
  listMuted: "var(--v2-light)",
  listAccent: "var(--v2-accent)",
  listChipBg: "var(--v2-blue-tint)",
  listTint: "var(--v2-tint)",
  listLine: "var(--v2-line)",
  listCard: "var(--v2-card)",
  listCardShadow: "var(--v2-shadow-card)",
  kpiToggleHoverBorder: "#cfe0fc", // Approved KPI control hover; independent of keyboard-key styling.
  listPinnedShadow: "0 2px 6px rgba(16,36,58,.07)",
  listOverflowFade: "linear-gradient(to right, transparent, var(--list-overflow-tint, var(--v2-tint)) 45%)",
  listOverflowSeam: "-10px 0 12px -8px rgba(16,36,58,.18)",
  listTransparent: "rgba(0, 0, 0, 0)",

  // Header gradient
  hdrFrom: "#0b3d6b",
  hdrMid:  "#1155a0",
  hdrTo:   "#1a73e8",
  tourInk:  "#122746",
  tourBlue: "#2f6df6",
  tourTint: "#eef4fe",
  tourPanel: "#f5f8fc",
  tourFooter: "#fafbfd",
  tourCtaBg:  "#f5a623",
  tourCtaInk: "#3a2a00",

  // Semantic colors

  // UI action accent — the primary interactive color for buttons, send
  // controls, icon sparkles, and accent borders across feature surfaces
  // (AskPopout send button, AdminPhotosPanel toggle pill, ReportTile link).
  // Same hex as T.blue today; kept as a distinct token so the two can diverge
  // independently (e.g. a future rebrand could shift T.blue for links while
  // keeping T.accent on interactive controls, or vice versa).
  accent: "#1a73e8",

  tblHdr:   "#1a73e8",
  blue:     "#1a73e8",
  blueSoft:       "#e8f0fe",
  blueSoftBorder: "#c5d5f8",  // border that pairs with T.blueSoft bg (e.g. Mutual badge)
  ycEditRowBg:    "#f0f7ff",  // pale-blue highlight for a YearCosts row being edited
  barTintBorder: "#85b7eb",
  dark:     "#1e293b",
  recordCloseBg: "#0f1b2d",
  searchExampleInk: "#2b3442", // quoted universal-search examples from the approved palette mockup
  mid:      "#64748b",
  light:    "#94a3b8",
  lightGray:"#f1f5f9",
  tableHeadBg: "#fafbfd", // near-white thead background used by heatmap/grid headers
  utilizationSparkAlt: "#cdd6e2",
  utilizationTrack: "#d7dce4",
  utilizationPeak: "#8fd3ae",
  utilizationPeakText: "#69b98d",
  utilizationUnused: "#aeb6c4",
  utilizationSourceBorder: "#bfe6cf",

  // ── Tooltip / Popover shared dark standard ─────────────────────────────────
  // Canonical dark surface for the shared <Tooltip>/<Popover> components
  // (src/components/Tooltip.jsx, src/components/Popover.jsx). Matches the
  // Offices List "Headroom"/"Space Fit" header tooltip reference style
  // (T.tooltip.bg / T.tooltip.fg) — canonical navy #1E3A5F. Bespoke tooltips
  // across the app use this token — see docs/style_guide.md §5.12.
  tooltip: {
    bg:     "#1E3A5F", // TOOLTIP-STANDARD canonical navy — matches --v2-tooltip-bg
    fg:     "#ffffff",
    shadow: "0 4px 12px rgba(0,0,0,0.18)",
    fontSize: TOOLTIP_FONT_SIZE,
    fontWeight: FW_REGULAR,
    lineHeight: TOOLTIP_LINE_HEIGHT,
  },
  homeKpiPopover: {
    label: "#9fb4d1",
    asOf: "#8fd0b0",
  },

  // ── Typography scale ──────────────────────────────────────────────────────
  // Full size ladder with half-step tokens for in-between sizes.
  // Use these instead of raw px values in all components.
  // Named by semantic tier; "5" suffix = half-step above the base tier.
  fsMin:    8,   // min  — ultra-dense micro-labels (provenance badge)
  fsMin5:   8.5, // min+ — half-step between fsMin and fsXxxs (e.g. column eyebrow labels)
  fsXxxs:   9,   // xxxs — dense timeline/badge labels, chevron chars, chart axis
  fsXxxs5:  9.5, // xxxs+— compact uppercase chips (month abbreviation)
  fsXxs:   10,   // xxs  — dense badge counters, compact pill text
  fsXxs5:  10.5, // xxs+ — score italic footnotes
  fsXs:    11,   // xs   — metadata, sub-labels, counters, badges, location ID
  fsXs5:   11.5, // xs+  — inline warning/error text, weight% labels
  fsSm:    12,   // sm   — secondary text, doc links, utility labels, empty states
  fsSm5:   12.5, // sm+  — compact buttons, filter/sort dropdowns, table cells
  fsBase:  13,   // base — body copy, field values, address text
  fsBase5: 13.5, // base+— card sub-headings, event titles
  fsMd:    14,   // md   — loading text, body-adjacent sizes
  fsMd5:   15,   // md+  — modal dialog titles, section headings, card labels
  fsLg:    16,   // lg   — section totals, prominent numbers
  fsLg5:   17,   // lg+  — admin panel labels, vibe vote buttons, sub-headings
  fsLg6:   18,   // lg++ — day/count display numbers, home stat counts
  fsLg7:   19,   // lg7  — concession card stat numbers (design comp spec)
  fsLgX:   20,   // lgX  — icon/close button text (between lg and xl)
  fsLgX5:  22,   // lgX+ — portfolio page headings, chart count displays
  fsLgX6:  23,   // lgX++— hero score / KPI value display
  fsXl:    24,   // xl   — KPI primary values
  fsXl5:   26,   // xl+  — service card KPI values, score ring numbers
  fsXl6:   28,   // xl++ — hero emoji/icon container, empty-state icons
  fs2xl:   32,   // 2xl  — hero KPI (sq ft, seats)
  fs2xl5:  36,   // 2xl+ — hero lease band KPI value
  fs3xl:   40,   // 3xl  — error boundary icon

  fwRegular:   FW_REGULAR, // regular    — body copy (xs / base)
  fwSemibold:  600,  // semibold   — prominent numbers, section totals
  fwDisplay:   600,  // display    — KPI/display numbers (ring centers, tiles, cost totals, stat cards)
  fwBold:      700,  // bold       — headings, non-data emphasis (card h3, section titles, badges)
  fwHubTitle:  800,  // Projects weekly-band heading only; not a display-number weight.
  fwExtrabold: 850,  // extra-bold — compact KPI tile values (variable-font intermediate weight)
  fwBlack:     900,  // black      — ultra-heavy micro-labels (compact tile uppercase eyebrow)
  // Note: weight 500 is retired; all existing 500 usages map to fwSemibold.
  // Note: weight 800 is retired from display-number use; map all display 800s to fwDisplay.

  // Status accents
  green: "#10b981",
  red:   "#dc2626",
  amber: "#f59e0b",

  // ── RAG scale §1b — the single official red/amber/green triad ───────────────
  // Used exclusively by the HowScoredDrilldown RAG bar segments and any other
  // surface that must agree with the style-guide §1b threshold colours.
  //
  // ragGreen matches T.greenDeep (#1f9d6b) — use T.greenDeep on that surface.
  // ragAmber and ragRed are distinct from T.amber (#f59e0b) and T.urgentRed
  // (#A32D2D) which serve different semantic surfaces.
  //
  // DO NOT consolidate ragAmber into T.amber — they are different amber shades
  // (style-guide §1b warm-golden vs. the legacy yellow-amber utility token).
  // DO NOT consolidate ragRed into T.urgentRed — different hue family.
  ragAmber: "#c98a00",  // §1b amber — RAG scale fill + status pills
  ragRed:   "#c2102e",  // §1b red   — RAG scale fill + status pills
  ragRedBg:     "#fae8eb",  // §1b pale red bg     — RAG Over cell bg / deficit badge bg
  ragAmberBg:   "#fef3c7",  // §1b pale amber bg   — RAG Tight cell bg / tight badge bg
  ragGreenBg:   "#e7f3ec",  // §1b pale green bg   — RAG Covered cell bg / surplus badge bg
  ragRedBorder:   "#f5a3a3",  // §1b red border    — RAG Over cell border
  ragAmberText:   "#a05c00",  // §1b amber text    — RAG Tight cell foreground (darker than ragAmber)
  ragAmberBorder: "#ffd97d",  // §1b amber border  — RAG Tight cell border
  ragGreenText:   "#1a6f2e",  // §1b green text    — RAG Covered cell foreground
  ragGreenBorder: "#7ecf8e",  // §1b green border  — RAG Covered cell border
  subleaseRecoveryBg:     "#f1faf5",
  subleaseRecoveryBorder: "#cfe6da",
  //
  // ── Canonical RAG-amber pill tokens (source of truth: Leases Outlook Negotiate/Model) ──
  // Every amber status pill/badge/chip in the app must use exactly this three-token set:
  //   background → T.ragAmberBg   (#fef3c7)
  //   color      → T.ragAmberText (#a05c00)   ← NOT T.amberDark, NOT T.negotiateOrange
  //   border     → T.ragAmberBorder (#ffd97d)  ← NOT T.yellowRing, NOT T.pillAmberBg
  // T.ragAmber (#c98a00) is the fill/dot/accent; T.ragAmberText is the readable text pair.
  // Surfaces out of scope: chart fills, map dots, score-band rings — those may keep T.ragAmber.

  // Feature-inventory / build-status status chip palette
  // Live (bright green — distinct from T.green which is used by utilGreenText)
  statusLive:           "#22c55e",
  statusLiveBg:         "#f0fdf4",
  // Mock (purple)
  statusMock:           "#a855f7",
  statusMockBg:         "#faf5ff",
  // Error / not-built red (lighter than T.red #dc2626; used for required-field
  // asterisks and "Not built" chips)
  errorRed:             "#ef4444",
  errorRedBg:           "#fef2f2",
  // Sign-off confirmed (deep green text + pale border)
  confirmedGreen:       "#16a34a",
  confirmedGreenBorder: "#86efac",
  // Sign-off needs-review / pending (amber text + pale border)
  pendingAmber:         "#d97706",
  pendingAmberBorder:   "#fcd34d",

  // KPI tile background — warm cream used by SpaceOverviewCard tiles and
  // LeaseTabTopSection StatCardLarge. Single-sourced here per L52.
  // Canonical hex: #F4F2EA (matches StatCardLarge legacy CSS variable fallback).
  kpiTileBg: "#F4F2EA",

  // Neutral tile background — cool light-gray for amenity tiles and other
  // compact grid cells that need a neutral (non-beige) chip background.
  // Distinct from kpiTileBg (#F4F2EA warm beige) which is reserved for
  // the lease/space KPI tile surfaces. Same hex as lightGray but with a
  // dedicated semantic name so renaming either never silently breaks the other.
  tile: "#f1f5f9",

  // Selected-card background fill + border — used by LedgerCard in the Leadership
  // Radar pane when a card is in the selected state. Replaces the former type-
  // coloured box-shadow ring with a subtle blue-tinted fill + faint border.
  // Distinct from T.blueSoft (#e8f0fe, saturated chip tint) and T.blueXLight
  // (#eff6ff, cool light blue) — this is a warmer, desaturated selection wash.
  selBg:     "#eff4fd",  // selected-card fill
  selBorder: "#cdd9ee",  // selected-card border (pairs with selBg)
  // ── Headcount Trend current-month column tint triad ─────────────────────
  // Soft blue washes for the latest-loaded-month column highlight (Option B).
  // Three distinct shades: header (lightest), data cells (light), total row
  // (slightly deeper). Paired with T.blue accent text and border.
  // Do NOT consolidate with T.blueSoft (#e8f0fe) or T.blueXLight (#eff6ff) —
  // different hue weight and semantic surface (column tint vs. badge/chip).
  hcCurrentHeader: "#eaf1fe",  // current-month header cell background
  hcCurrentCell:   "#f4f8ff",  // current-month data/market/office cell background
  hcCurrentTotal:  "#e6f0fe",  // current-month portfolio total cell (deeper tint)

  planRowBg:        "#fbfcfe",  // Plan Matrix open-but-not-expanded row background (near-white, no purple tint)
  planExpandBorder: "#dbe6f5",  // ring border on box-shadow when a Plan Matrix row is expanded
  planExpandBg:     "#ffffff",  // accordion expanded-content panel background
  planExpandDivider:"#f1f5f9",  // divider line (border-top) inside the accordion content panel
  planSummaryChipBg:    "#f8fafc",  // seats/utilization summary footer chip background
  planSummaryChipBorder:"#eef1f5",  // seats/utilization summary footer chip hairline border
  planTabDescActiveFg:  "#5a7fc4",  // active tab descriptor text — muted blue under the bold tab label (PlanTabBar)
  // UnderlineTabBar inactive label + hover treatment (Option A rail).
  // tabInactiveFg: stronger resting label color than T.mid (#64748b) — the
  //   mid token is intentionally muted for secondary text; inactive tabs need
  //   a slightly louder slate-blue to register as interactive. Do NOT
  //   consolidate with T.mid — they serve different semantic surfaces.
  // tabHoverBg: light cool-gray wash shown when the user hovers an inactive tab.
  //   Distinct from T.hoverBg (#f8fafc, transient row hover) and T.rowActive
  //   (#eaf1fe, persistent selected state) — this is a tab-specific hover tint.
  //   Do NOT consolidate with T.hoverBg.
  tabInactiveFg: "#5b6b7c",  // inactive tab label — stronger slate-blue resting color
  tabHoverBg:    "#f4f7fb",  // inactive tab hover wash — cool near-white blue tint

  // UnderlineTabBar badge pill — two states; active pairs with T.accent text.
  tabBadgeActiveBg:   "#dfe9ff",  // active tab badge background (pale blue)
  tabBadgeInactiveBg: "#eef3f8",  // inactive tab badge background (cool near-white)
  tabBadgeInactiveFg: "#5b6b7c",  // inactive tab badge foreground (slate-blue)

  // InsightCallout banner gradients — pressure (maroon/wine) + growth (teal).
  // Gradient strings are restricted to tokens.js per L52; hex appears here only.
  insightPressureGrad: "linear-gradient(120deg, #7d1f2a, #ae3d46)",
  insightGrowthGrad:   "linear-gradient(120deg, #0a5c66, #0d8a9e)",

  // Plan market panel — photo thumbnail gradient fallbacks + completeness block
  // Gradient strings are restricted to tokens.js per L52; hex appears here only.
  // AMER/EMEA/APAC: deep navy → midnight for each region flavour.
  heroNavyGrad:   "linear-gradient(135deg, #132a4a, #0e2038)",  // Dates tab Next-decision hero card (mockup exact value)
  heroKickerBlue: "#9fb6d6",  // Dates hero kicker text — muted blue on dark navy gradient
  heroSubBlue:    "#bcd0ec",  // Dates hero sub-line text — lighter muted blue on dark navy gradient
  photoGradAmer:  "linear-gradient(135deg, #1a3a5c, #0f2540)",
  photoGradEmea:  "linear-gradient(135deg, #2d1b4e, #1a0f2e)",
  photoGradApac:  "linear-gradient(135deg, #0f3d2e, #071f18)",
  photoPlaceholder: "#eef2f7",  // light neutral fallback for missing office thumbnails
  photoScrim:     "rgba(10,18,36,.55)",  // dark scrim overlay on photo thumbnail bottom 40%
  planCompBorder: "#e8edf2",  // plan completeness block border (slate-100 blue-gray tint)
  planCompFill:   "#334155",  // progress bar fill + chip set-color (slate-700)

  // Stat accents
  statCyan:   "#38bdf8",
  statGreen:  "#6ee7b7",
  statYellow: "#fbbf24",
  statBlue:   "#93c5fd",
  statPink:   "#f9a8d4",  // legacy "Available Seats" stat (slice U2)

  // White overlays
  white:       "#fff",
  whiteHalf:   "rgba(255,255,255,0.55)",
  whiteMuted:  "rgba(255,255,255,0.6)",
  whiteHover:  "rgba(255,255,255,0.88)",
  whiteLine:   "rgba(255,255,255,0.20)",
  whiteFade:   "rgba(255,255,255,0.45)",  // page-context label in header bar
  whiteStrong: "rgba(255,255,255,0.80)",  // strong text on dark bg ("Welcome, …")

  // NavRail (sidebar) overlays — match legacy values exactly
  navItemMuted:  "rgba(255,255,255,0.45)",
  navItemWarm:   "rgba(255,220,170,0.70)",  // warm cream — resting icon/label colour (warmer than navItemMuted)

  // NavRail active pill — muted blue-gray background for the selected nav
  // button. Pairs with T.hdrFrom (#0b3d6b) as the icon/label color.
  navActivePill: "#e4eef6",

  // Generic dropdown / popover surfaces (originally added for the
  // RoleSwitcher; retained because FilterDropdown and other popovers
  // depend on shadowSm and blueXLight).
  shadowSm:     "0 4px 24px rgba(0,0,0,0.18)",
  shadowModal:  "0 10px 40px rgba(0,0,0,0.25)",  // elevated dialog shadow — deeper than shadowSm
  modalOverlay: "rgba(0,0,0,0.45)",  // standard dark backdrop behind modal dialogs

  // ── Shadow scale — all remaining box-shadow literals centralised here ────────
  // Names indicate vertical offset tier then opacity weight.
  // Navy variants use rgba(16,36,58,…) for a warmer dark tone (ask-portfolio).
  // Directional variant (upward) noted explicitly.
  shadowToggle:      "0 1px 2px rgba(0,0,0,0.18)",    // toggle track (ExperienceTab)
  shadowSegment:     "0 1px 2px rgba(16,36,58,0.12)",  // segment-control active button (CostBasisToggle)
  shadowTileXs:      "0 1px 3px rgba(0,0,0,0.06)",    // low-elevation board/card tile
  shadowTileSm:      "0 1px 3px rgba(0,0,0,0.08)",    // chip / value-badge elevation
  shadowTileMd:      "0 1px 3px rgba(0,0,0,0.10)",    // timeline event dot / row hover
  shadowToggleActive:"0 1px 3px rgba(0,0,0,0.12)",    // view-mode toggle pressed state
  shadowTileLg:      "0 1px 3px rgba(0,0,0,0.15)",    // overview-card tile
  shadowButton:      "0 1px 3px rgba(0,0,0,0.3)",     // action button (tile / photo)
  shadowTableHdr:    "0 2px 6px rgba(0,0,0,0.06)",    // sticky table / list header row
  shadowCardXs:      "0 2px 8px rgba(0,0,0,0.06)",    // compact card / error boundary
  shadowCardSm:      "0 2px 8px rgba(0,0,0,0.10)",    // tooltip fixture / small popover
  shadowCardMd:      "0 2px 12px rgba(0,0,0,0.08)",   // auth page card (login, access-denied)
  shadowCardXsB:     "0 2px 8px rgba(0,0,0,0.08)",    // card hover lift (comms list row)
  shadowHover:       "0 4px 12px rgba(0,0,0,0.08)",   // hover-state lift (report tile)
  shadowHoverStrong: "0 4px 12px rgba(0,0,0,0.12)",   // stronger hover lift (signals board row)
  shadowTooltip:     "0 4px 12px rgba(0,0,0,0.10)",   // provenance tooltip panel
  shadowTooltipDark: "0 4px 12px rgba(0,0,0,0.18)",   // TOOLTIP-STANDARD dark Tooltip/Popover shadow
  shadowPanel:       "0 4px 16px rgba(0,0,0,0.08)",   // error-boundary / info panel
  shadowEditModal:   "0 4px 20px rgba(0,0,0,0.18)",   // edit-form modal / drawer
  shadowDropdown:    "0 8px 24px rgba(0,0,0,0.12)",   // filter / sort dropdown menu
  shadowFloat:       "0 8px 32px rgba(0,0,0,0.14)",   // floating actions panel
  shadowFeedbackHover: "0 12px 36px rgba(0,0,0,0.20)", // feedback launcher hover lift
  shadowUpTray:      "0 -4px 12px rgba(0,0,0,0.06)",  // sticky footer tray (upward shadow)
  shadowAsk:         "0 18px 50px rgba(16,36,58,.22)", // ask-portfolio popout resting state
  shadowAskWide:     "0 24px 64px rgba(16,36,58,.30)", // ask-portfolio popout expanded state
  shadowMega:        "0 24px 64px rgba(0,0,0,0.22)",   // deep shadow (comm form)
  shadowPlanCardExpanded: "0 20px 44px rgba(18,39,70,.20), 0 4px 10px rgba(18,39,70,.10)", // Market Plan expanded-row elevated card
  shadowTimelineKeyPopover: "0 2px 6px rgba(16,30,54,.08), 0 18px 44px rgba(16,30,54,.18)", // borderless Leases Timeline Key popover elevation
  shadowTimelineKeyCaret:   "-2px -2px 4px rgba(16,30,54,.05)", // subtle matching shadow on the Timeline Key popover caret
  shadowActivePill:  "0 0 0 1px rgba(255,255,255,.12), 0 4px 14px rgba(31,157,107,.45)", // header active-stage pill glow
  editorBlue:  "#2563eb",
  blueXLight:  "#eff6ff",

  // Plan Matrix expanded-row border — pale blue border that pairs with the
  // T.editorBlue (#2563eb) left accent stripe on the Direction-B expanded
  // accordion row wrapper. Distinct from T.blueSoftBorder (#c5d5f8, more
  // saturated) and T.planExpandBorder (#dbe6f5, different surface/shade).
  planRowExpandedBorder: "#cddffb",

  // Faint blue-white header wash for the expanded Plan Matrix row's main
  // row strip (PLAN-MARKET-LIST-REDESIGN-S1). Deliberately NOT T.askScopeBg
  // (#eaf1fe, a periwinkle/lavender tint) — that color was flagged in design
  // review as the "lavender tint" to remove from the expanded-row treatment.
  planRowHeaderBg: "#f0f7ff",

  // Utilization backgrounds + paired foreground text
  utilRed:       "#fee2e2",
  utilAmber:     "#fffbeb",
  utilAmberBorder: "#f5d77a",
  utilGreen:     "#dcfce7",
  utilGreenText: "#166534", // green-800 — paired dark text for utilGreen badge backgrounds

  // ── Utilization-panel chart colours ──────────────────────────────────────
  histfill:    "#d9e6f6",  // monthly bar fill (light blue)
  utilGold:    "#d9a400",  // peak-day marker dot
  utilOpen:    "#cdd9e6",  // open (unassigned) seats in waffle chart
  utilMaxRoom: "#eef2f7",  // max-density cells beyond installed capacity

  // Red theme accents (legacy lines 156-157). redLight = mid red border;
  // redSoft = pale red panel background. Used by Lease Intelligence's
  // "Needs Your Attention" populated state and other urgency surfaces.
  redLight: "#f87171",
  redSoft:  "#fef2f2",

  // Admin alert banners (DataSection inline error/success toasts).
  // alertErrBg: lightest red wash (#fff5f5) for error alert backgrounds.
  // alertErrBorder: red-200 border (#fecaca) that pairs with alertErrBg —
  //   used by ImportReviewPanel skip-row and partial-apply banners.
  // alertSuccessBg / alertSuccessBorder: lightest green wash + mid border
  // (#f0fff4 / #68d391) for success alert backgrounds.
  // Distinct from T.redSoft (#fef2f2) and T.greenSoft (#ecfdf5) which serve
  // status-badge / panel semantics — these are notification-banner-specific.
  alertErrBg:        "#fff5f5",
  alertErrBorder:    "#fecaca",
  alertSuccessBg:    "#f0fff4",
  alertSuccessBorder: "#68d391",

  // Card interaction
  hoverBg:     "#f8fafc",
  // Consolidated selected / active-row token — used wherever a row is in a
  // persistent "open" or "active" state (Offices peek drawer open, Market Plan
  // accordion expanded, HC Trend target row). Matches --v2-blue-tint (#eaf1fe).
  // Distinct from hoverBg (#f8fafc, transient hover) and blueSoft (#e8f0fe,
  // badge/chip fills). Do not use for transient hover; use hoverBg instead.
  rowActive:   "#eaf1fe",
  cardShadow:  "0 1px 4px rgba(0,0,0,0.05)",

  // Purple
  purple:   "#8b5cf6",
  purpleBg: "#f5f3ff",

  // Decision chip — violet-600 fg + violet-100 bg used by LocationDrawerV2
  // for the "Decision" type chip on critical-date timeline entries.
  // Distinct from T.purple (#8b5cf6, general purple) and T.statusMock
  // (#a855f7, purple-500 feature-status chip) — darker violet family.
  // Do NOT consolidate with T.purple — different hue weight and semantic.
  decisionFg: "#7c3aed",  // violet-600 — Decision chip text
  decisionBg: "#ede9fe",  // violet-100 — Decision chip background

  // ── Lease tab hero KPI accent colors ─────────────────────────────────────
  // These are semantically distinct from the existing utilGold (#d9a400,
  // peak-day Gantt marker) and opexSecFg (#0d8a9e, Security dept bar).
  // Do NOT remove T.opexSecFg or T.utilGold — different surfaces and semantics.
  gold:  "#d3a000",  // blended-rate KPI accent in LeaseTabHeroBar
  teal:  "#0d8a9e",  // accent color (LeaseTabHeroBar no longer uses this; available for future KPIs)

  // ── Strategy Band golden header (Option B) ────────────────────────────────
  // Three-token triad for the StrategyBand header row on the Plan view.
  // Distinct from T.gold (#d3a000) which is a deeper amber used for the lease
  // hero bar KPI. These are a softer warm-gold family sourced from the v2
  // design mockup — do NOT consolidate into T.gold.
  strategyHeaderBg:     "#fbf1d4",  // soft gold tint — header row background
  strategyHeaderBorder: "#e6c15a",  // warm gold — header bottom border
  strategyLabelColor:   "#c79212",  // golden brown — "◆ Strategy" label text

  // ── Policy Band blue-tint tokens ─────────────────────────────────────────
  // Distinct from T.blueSoft (#e8f0fe, saturated chip tint) and T.blueXLight
  // (#eff6ff, lighter blue wash). These are the precise blue-tint family for
  // the Market Plan Policy Band shell and cell borders per the comp spec.
  // Do NOT consolidate with T.blueSoft or T.blueXLight.
  policyBandBg:         "#eef3fb",  // band shell background — soft blue tint
  policyBandBorder:     "#d8e4f6",  // band shell + section divider border
  policyBandCellBorder: "#e3ebf7",  // individual knob cell border in expanded grid

  // ── Pulse KPI bar accent colors ───────────────────────────────────────────
  // greenDeep: deep green for Sq Ft KPI bar cell and positive delta values.
  // Distinct from T.green (#10b981, operational stage dot / utilization badge)
  // and SCORE_BAND_ACCENT.strong (same hex but consumed via the band map, not T).
  // Do NOT consolidate with T.green — different semantic surface.
  // purpleDeep: deep purple for Annual Occupancy KPI bar cell.
  // Distinct from T.purple (#8b5cf6, general purple) — darker, accent-weight.
  greenDeep:  "#1f9d6b",  // Sq Ft KPI accent + DeltaCell positive direction
  purpleDeep: "#7a4fc0",  // Annual Occupancy KPI accent

  // ── Pulse category chip teal family ──────────────────────────────────────
  //
  // tealSoftBg: extremely pale blue-green chip background for teal-category
  // surface chips (Pulse page category classification chips). Distinct from
  // T.opexSecBg (#e2f3f5, Security dept tint) and T.tint (#f5f8fc, hero bar
  // tint) — slightly warmer/greener undertone. Do NOT consolidate.
  //
  // tealDarkFg: dark teal foreground text that pairs with tealSoftBg.
  // Distinct from T.teal (#0d8a9e, KpiBar / lease-count accent) — this is a
  // desaturated, darker teal suitable for small-text chip labels.
  // Do NOT consolidate with T.teal — different saturation and semantic surface.
  tealSoftBg: "#eef3f4",  // pale blue-green — category chip background
  tealDarkFg: "#0a6b7a",  // dark desaturated teal — category chip foreground

  // ── Lease action bucket palette ───────────────────────────────────────────
  //
  // Shared foreground/tint pairs for the four action buckets. These are the
  // source of truth for both the Leases timeline and Home action windows;
  // keep bucket-specific styling out of v2/tokens.css so the surfaces cannot
  // drift apart.
  leaseActionBuckets: {
    "decide-now":      { foreground: "#d5493f", tint: "#fbeceb" },
    negotiate:         { foreground: "#c8860a", tint: "#fbf3e2" },
    "reduce-exposure": { foreground: "#7c5cbf", tint: "#f1ecfb" },
    monitor:           { foreground: "#1f9d57", tint: "#e8f6ee" },
  },

  // ── Portfolio Command opportunity card visual treatments ────────────────
  // Canonical-token gradients for portfolio-wide remediation actions. Market
  // opportunities use the market/location photo when one is available.
  commandGradHeadcount:    "linear-gradient(135deg, #0f3d5e, #167c8c)",
  commandGradLease:        "linear-gradient(135deg, #2d1b4e, #6b3f83)",
  commandGradAbstraction:  "linear-gradient(135deg, #173f35, #257a61)",
  commandGradDefault:      "linear-gradient(135deg, #132a4a, #33526f)",

  // ── Pulse attendance efficiency lens ────────────────────────────────────
  attendanceUsed:      "#2f8f5b", // average-day used seats and utilization headline
  attendancePeakSwing:  "#9bd3b4", // seats used between average and peak
  attendanceNeverUsed:  "#dfe4ec", // seats above peak attendance

  // ── LeaseWindowsCalPane calendar band tile colors ────────────────────────
  //
  // Soft solid fills for the month-band background cells in the lease-action
  // windows calendar. Each corresponds to one action-window category (r=red/
  // Decide, a=amber/Model, b=blue/Negotiate). These are intentionally softer
  // (more desaturated) than the RAG pill colors so the calendar grid reads as
  // a background texture rather than a status indicator.
  //
  // Do NOT consolidate with T.ragRedBg/T.ragAmberBg/T.actionWindowNegotiateBg —
  // those are pill backgrounds; these are calendar-tile fills (different shade
  // weight and semantic surface).
  bandTileRed:   "#d98a8a",  // soft coral-red — Decide band tile
  bandTileAmber: "#d9ab63",  // soft amber-gold — Model band tile
  bandTileBlue:  "#9bb3e6",  // soft periwinkle — Negotiate band tile

  // ── Pulse Lease Action Windows pill colors ────────────────────────────────
  //
  // Three action-window pills: Decide (red family), Model (watchlist-amber
  // family), Negotiate (blue family). Each pill has a soft bg tint + a dark
  // fg for legible small text.
  //
  // actionWindowNegotiateBg / Fg: blue-50 bg + blue-700 fg for "Negotiate"
  // window pill. Distinct from T.blueSoft (#e8f0fe, saturated blue chip) and
  // T.blueXLight (#eff6ff, cool blue). Do NOT consolidate — different hue
  // family and semantic (action-window pill vs. badge chip).
  // "Decide" pill uses T.ragRedBg (bg) + SCORE_BAND_FG.critical (fg).
  // "Model" pill uses SCORE_BAND_BG.watchlist (bg) + SCORE_BAND_FG.watchlist (fg).
  actionWindowNegotiateBg: "#e8f1ff",  // blue-50 — Negotiate action window pill bg
  actionWindowNegotiateFg: "#155dc2",  // blue-700 — Negotiate action window pill fg

  // ── SpaceHeadroomPane chart + panel colors ────────────────────────────────
  //
  // capacityBarTrackBg: light blue-gray track background for the capacity-
  // usage bar rows in SpaceHeadroomPane. Distinct from T.tealSoftBg (#eef3f4)
  // which has a slightly warmer/greener hue. Do NOT consolidate.
  //
  // scenarioPanelGradient: right-panel card gradient in SpaceHeadroomPane.
  // Subtle top-to-bottom wash from pure white to a near-white blue (#fbfcfe).
  // Not a color token — a full gradient string consumed directly in `background`.
  capacityBarTrackBg:    "#eef3f8",                                       // capacity-usage bar track
  scenarioPanelGradient: "linear-gradient(180deg, #fff, #fbfcfe)",        // SpaceHeadroomPane right panel
  drawerPhotoGradient:   "linear-gradient(135deg, #5b7da6 0%, #33526f 100%)", // LocationDrawerV2 photo placeholder bg

  // Stage badge colors
  buildBlue:       "#0ea5e9",
  buildBlueBg:     "#e0f2fe",
  // WHATIF-BUILD-ONLINE-INDICATOR-S1: blue used for in-build capacity segment
  // on the What-If Scenario bar (hatch fill + post-build installed line).
  // Distinct from T.buildBlue (#0ea5e9, sky-500 — stage badge) and T.teal —
  // this is blue-600 (#2f6feb), matching the reference comp treatment.
  whatifBuildBlue:    "#2f6feb",
  // Light stripe of the repeating-linear-gradient hatch — blue-100 (#cfe0fb).
  whatifBuildHatch:   "#cfe0fb",
  negotiateOrange: "#f97316",
  negotiateBg:     "#fff7ed",
  yellowRing:      "#fcd34d",
  redRing:         "#fca5a5",
  greenSoft:       "#ecfdf5",

  // ── Plan Matrix strategy-pill border triad ─────────────────────────────────
  //
  // One border token per strategy whose fg/bg color is not fully self-contained
  // in an existing token pair.  These are sky-200 / orange-200 / teal-200 —
  // the natural mid-weight border shade for each family.
  //
  // buildBlueBorder: sky-200 — Expand strategy pill border.
  //   Pairs with DISPOSITION_META.expand.bg (sky/blue family).
  //   Distinct from T.buildBlueBg (#e0f2fe, sky-100) and T.blueSoftBorder
  //   (#c5d5f8, blue-300) — different hue family and weight.
  buildBlueBorder: "#bae6fd",
  //
  // negotiateBorder: orange-200 — Consolidate strategy pill border.
  //   Pairs with T.negotiateBg (#fff7ed, orange-50).
  //   Distinct from T.negotiateOrange (#f97316, orange-500) — lighter border.
  negotiateBorder: "#fed7aa",
  //
  // dispRelocateBorder: teal-200 — Relocate strategy pill border.
  //   Pairs with DISPOSITION_META.relocate.bg (teal family).
  //   Distinct from T.dispRelocateFg (#0d8a9e, teal-700) and T.dispRelocateBg
  //   (#e4f4f6, teal-50) — mid-weight border shade. Do NOT consolidate.
  dispRelocateBorder: "#9dd0d8",

  // LSE-VACATED — Vacated stage pill. Repaletted to muted violet (G-11)
  // so Vacated reads as "held-parked" rather than amber-adjacent.
  // Previous values (audit trail): vacatedBg #efe5d4, vacatedFg #6b4f2b,
  // vacatedBorder #d4c5b0 (warm tan). New values: violet-100 bg / violet-700
  // fg / violet-300 border — distinct from green (operational), blue
  // (in-build), amber (At Risk / health=yellow), red (critical), and
  // light-gray (Retired). DO NOT consolidate back into T.amber for "DRY" —
  // the two semantics are visually distinct on purpose. See LSE-VACATED doc.
  // vacatedBorder: violet-300 border that pairs with vacatedBg/vacatedFg
  // in LeasesListTable status chips. Distinct from T.borderDark (#cbd5e1).
  vacatedBg:       "#ede9fe",
  vacatedFg:       "#6d28d9",
  vacatedBorder:   "#c4b5fd",

  // Lease portfolio Gantt bar colors (LSE-LANDING-PAGE-FOLLOWUP-1)
  // leaseGanttGreen is intentionally muted vs T.green so Gantt bars read
  // differently from health-dot and operational-stage surfaces.
  // Do NOT merge back into T.green — different semantic.
  // URGENT_UNDER_1 bar uses T.urgentRed (= leaseTabConfig.URGENT_RED).
  // UPCOMING_1_TO_3 bar uses T.amber (= URGENT_AMBER there).
  // EXPIRED bucket now uses T.defaultDot (LSE-LEASES-URGENCY-EXPIRED-GRAY-MATCH-LOCATIONS).
  leaseGanttGreen:  "#5BAA3A",

  // ── LSE-STYLE-NORMALIZE-1 additions (INC-01, 04, 06, 07) ────────────────

  // Contractual-urgency red (INC-01). Routes leaseTabConfig.URGENT_RED here.
  // Do NOT consolidate with T.red (#dc2626) — T.red is for error/health
  // surfaces; urgentRed is the specific dark-red for lease-expiry countdown.
  urgentRed: "#A32D2D",

  // Amber dark — warm fg text on light-amber surfaces (INC-04).
  // DaysRemainingPill 1–365 d range, DecisionsAheadCallout heading,
  // SpacebaseBanner unlinked CTA, abatement type pill,
  // LeaseTimeline renewal_notice marker.
  // Pairs with T.utilAmber (#fffbeb) or T.amber (#f59e0b) backgrounds.
  amberDark: "#854F0B",

  // Spacebase system blue (INC-06).
  // Provenance S-badge dot, TI Allowance type pill, allowance/cam_audit
  // timeline markers, AccordionFieldPanel "fill gaps" CTA.
  // Distinct from T.blue (#1a73e8) which is the UI action/link blue.
  spacebaseBlue: "#185FA5",

  // Computed-provenance badge dot (INC-07).
  // Drives the "C" badge dot in ProvenanceTooltip. Shares hex #9B9994 with
  // the now-removed leaseGanttExpired token; kept as its own token for
  // semantic separation (provenance surface vs. Gantt bar fill).
  provenanceComputed: "#9B9994",

  // ── Task-427 token additions ─────────────────────────────────────────────
  //
  // These were promoted from inline hex across AddLocationModal, LocationsMap,
  // MigrationPanel, DaysRemainingPill, CriticalDateRow, ProvenanceBadge, and
  // DataSection per the LSE-STYLE-NORMALIZE-1 pattern. See docs/style_guide.md
  // §11 for the full resolution record.

  // Modal header gradient — deep navy start color for the AddLocationModal
  // blue header gradient. Distinct from T.hdrFrom (#0b3d6b) which is the
  // app navigation bar gradient; the modal uses a lighter deep-blue start.
  modalHeaderFrom: "#1e3a8a",

  // Success card chrome — used by MigrationPanel success result card.
  // bg uses T.greenSoft (#ecfdf5) which already existed.
  successBorder: "#86efac",  // bright green-300 border
  successText:   "#065f46",  // dark green-800 text on greenSoft bg
  navLiveBg:     "#d1fae5",  // emerald-100 — live status pill background

  // Pill / tag backgrounds — shared across DaysRemainingPill and
  // CriticalDateRow bucket tag chips. Each pairs with a matching fg token.
  pillRedBg:   "#FCEBEB",  // red range: overdue, break_option bg
  pillAmberBg: "#FAEEDA",  // amber range: 1–365 d remaining, expiration bg
  pillGreenBg: "#EAF3DE",  // healthy range: >365 d remaining, deposit bg

  // Forest green — dark green fg for healthy-range pill text, deposit_burndown
  // and test_fit type tags, and AccordionFieldPanel links. Intentionally darker
  // than T.green (#10b981) which is the operational stage dot / positive KPI.
  forestGreen: "#3B6D11",

  // Provenance palette backgrounds — light tint surfaces for type chips and
  // provenance badge circles. fg tokens follow.
  provenanceSpacebaseBg: "#E6F1FB",  // light blue — S badge, TI Allowance, allowance_deadline
  provenancePdfBg:       "#FEF3DC",  // light amber — P badge, escalation, moving_allowance
  provenanceManualBg:    "#E7F3EC",  // light green — M badge
  warmBeige:             "#F1EFE8",  // warm neutral — C badge computed, other/allowance warning

  // Provenance / bucket foreground text colors
  provenancePdf:         "#7A5C1E",  // dark amber-brown — P badge fg, escalation fg, moving_allowance fg
  provenanceManual:      "#1A6F2E",  // dark green — M badge fg (Manual entry)
  provenanceBadgeManual: "#d98324",  // orange — filled M badge circle in IngestionHistoryPanel ProvenanceBadge

  // Bucket-specific tag foreground colors for CriticalDateRow
  breakRed:        "#991B1B",  // dark crimson — break_option fg
  expirationAmber: "#7A5C1E",  // dark amber-brown — expiration fg

  // Pending-imports active state — DataSection "Pending imports" button when
  // count > 0. bluePale is the bg tint; blueDeep is the text/border color.
  bluePale: "#dbeafe",  // blue-100 — active badge background
  blueDeep: "#1e40af",  // blue-800 — active badge text

  // ── Experience hero bar tint ──────────────────────────────────────────────
  // Light-blue tinted panel background for the ExperienceHeroBar score zone.
  // Distinct from T.blueSoft (#e8f0fe) which is a saturated blue chip tint.
  // T.tintBorder is the matching 1px border for the tinted panel.
  tint:       "#f5f8fc",
  tintBorder: "#e6edf6",

  // Hero bar KPI-cell hairline — the `--line` token from the reference design.
  // Used for between-cell border-right hairlines in hero bars
  // (ExperienceHeroBar, LeaseTabHeroBar). Slightly lighter than T.border
  // (#e2e8f0), which is reserved for panel/card chrome. Do NOT consolidate.
  line: "#e7ebf0",

  // Defaults
  defaultDot:   "#ccc",
  defaultDotBg: "#f5f5f5",

  // ── COST-TAB-BUILD: OpEx department palette ──────────────────────────────
  //
  // Lease category deep-slate — foreground color for the Lease ledger row
  // dot and bar in both the per-location CostTab and the portfolio
  // PortfolioCostCard. Distinct from T.dark (#1e293b, body text) and
  // T.hdrFrom (#0b3d6b, header gradient start).
  opexLeaseFg: "#1f3a5f",  // deep slate — Lease category dot + bar fill

  // Security department teal — foreground/background pair for the Security
  // bar segment and dot in the Cost tab. Distinct from T.statCyan (#38bdf8)
  // which is a lighter, legacy stat-card accent.
  opexSecFg: "#0d8a9e",  // teal fg — Security dept dot, bar segment fill
  opexSecBg: "#e2f3f5",  // teal bg — Security dept chip/tint background

  // Synth block chrome — "Fully-loaded occupancy" card inside the Cost tab.
  // opexSynthBg is an extremely light blue distinct from T.blueXLight (#eff6ff).
  // opexSynthBorder is the matching light blue border.
  opexSynthBg:     "#f7faff",  // very-light blue bg for the synth block
  opexSynthBorder: "#dce8fb",  // light blue border for the synth block

  // ── Task-658 token additions ─────────────────────────────────────────────
  //
  // Promoted from inline hex across 21 component files per the L52 hex sweep.
  // Covers CSS-var fallback colors, provenance tooltip shades, bulk-action
  // palette, diff-row state backgrounds, and upload-panel status colors.

  // ── Warm design-system CSS-var fallback colors ───────────────────────────

  // Warm gray border — fallback for --color-border-tertiary. Distinct from
  // T.border (#e2e8f0) which is cool blue-gray; this is a warm off-white gray.
  warmBorder: "#E5E3DB",

  // Warm gray secondary border — fallback for --color-border-secondary.
  // LeaseTimeline tooltip borders + GRAY variable. Slightly darker than
  // T.warmBorder (#E5E3DB). Do NOT consolidate with T.border (#e2e8f0).
  warmBorderSecondary: "#D7D5CE",

  // ── Provenance tooltip colors (distinct from ProvenanceBadge shades) ─────

  // Manual source badge — green filled circle in ProvenanceTooltip and
  // DiffRow SOURCE_BADGE_STYLE. Distinct from T.provenanceManual (#1A6F2E)
  // which is the fg text color in ProvenanceBadge (a different component).
  provenanceTooltipManual: "#1A7F37",

  // PDF abstraction source badge — amber filled circle in ProvenanceTooltip
  // and DiffRow. Distinct from T.provenancePdf (#7A5C1E) which is the fg
  // text on light-amber chips.
  provenanceTooltipPdf: "#C98A00",

  // ── Diff-row and bulk-action state colors ────────────────────────────────

  // Green-50 background for accepted / applied diff rows and incoming value
  // cells (DiffRow). Lighter than T.greenSoft (#ecfdf5).
  acceptedRowBg: "#f0fdf4",

  // Green-200 border for bulk "accept" action buttons (BulkActionBar generic
  // accept_recommended / accept_all buttons). Distinct from T.successBorder
  // (#86efac, green-300) which is used for filled accept chip borders.
  greenBorder: "#a7f3d0",

  // Green-200 badge background for the BulkActionBar accept count badge and
  // incoming value cell border in DiffRow. Slightly darker than T.greenBorder.
  greenBadge: "#bbf7d0",

  // Red-200 badge background for the BulkActionBar reject count badge.
  // Lighter than T.utilRed (#fee2e2) and T.redRing (#fca5a5).
  redBadge: "#fecaca",

  // Yellow-200 border for mid-urgency (13–24 month) lease countdown pills in
  // MarketPlanPage. Pairs with T.ragAmberBg (#fef3c7) fill and T.ragAmber
  // (#c98a00) text. Sits between T.redBadge (#fecaca) and T.greenBadge
  // (#bbf7d0) in the urgency border triad. Distinct from T.warnYellowBorder
  // (#ffe082, yellow-300) — do NOT consolidate.
  amberBadge: "#fef08a",

  // Yellow-100 background for "in review" status pill (PendingImportsQueue)
  // and "skip" recommendation chip (DiffRow).
  warnYellowBg: "#fef9c3",

  // Amber-100 background for dirty-state warning banner (ImportSessionReview)
  // and "blocked" status in staged-row display. Slightly more saturated
  // than T.warnYellowBg (#fef9c3).
  warnAmberBg: "#fef3c7",

  // Amber-200 border for inline warning banners that sit on T.warnAmberBg.
  // Tailwind amber-200 (#fde68a). Do not conflate with T.pendingAmberBorder
  // (#fcd34d, amber-300) which is for pill/badge outlines.
  warnAmberBorder: "#fde68a",

  // ── Bulk-action "promote" palette (violet family) ─────────────────────────

  // Violet-100 background for the bulk "promote" action button.
  promoteBg: "#ede9fe",

  // Violet-300 border for the bulk "promote" action button.
  promoteBorder: "#c4b5fd",

  // Violet-800 text for the bulk "promote" action button.
  promoteText: "#5b21b6",

  // Violet-200 badge background for the bulk promote count badge.
  promoteBadgeBg: "#ddd6fe",

  // ── Error and blocked-state colors ────────────────────────────────────────

  // Red-700 error text — used for inline error messages in ImportSessionReview
  // and PendingImportsQueue. Darker than T.red (#dc2626, red-600) which is
  // for health dots and icon accents; do not consolidate.
  errorText: "#b91c1c",

  // Amber-800 text for "blocked" file-type warnings and the reload CTA in
  // ImportSessionReview. Near T.amberDark (#854F0B) but warmer and used on
  // different background shades.
  blockedText: "#92400e",

  // ── Decisions callout chrome ──────────────────────────────────────────────

  // Amber-50 background for DecisionsAheadCallout "card" variant.
  decisionsCardBg: "#FFF8E1",

  // Golden amber border for DecisionsAheadCallout "card" variant.
  decisionsCardBorder: "#F5D77A",

  // ── Lease timeline active bar ─────────────────────────────────────────────

  // Medium blue for the OngoingLeaseBar progress fill and start tick.
  // Distinct from T.spacebaseBlue (#185FA5, system blue) and T.blue (#1a73e8,
  // UI action blue). Do NOT merge — different semantic surface.
  timelineBlue: "#378ADD",

  // ── Upload / staging status panel colors ─────────────────────────────────

  // Mint green panel background for upload "done" status.
  statusDoneBg: "#e6f9ed",

  // Mid green border for upload "done" status panel.
  statusDoneBorder: "#a7e6bf",

  // Dark green text for upload "done" status panel.
  statusDoneText: "#1a7a40",

  // Yellow-300 border for upload warning status panel.
  warnYellowBorder: "#ffe082",

  // Dark amber-brown text for upload warning status panel.
  warnAmberText: "#7c5e00",

  // ─── PLAN-S1: Disposition module — relocate teal family ──────────────────
  // Only 2 new tokens needed. All other disposition colors reuse existing
  // T tokens (green/greenSoft, buildBlue/buildBlueBg, purple/purpleBg,
  // amberDark/utilAmber, red/redSoft, mid/lightGray).
  dispRelocateFg: "#0d8a9e",   // teal-700 — relocate foreground text
  dispRelocateBg: "#e4f4f6",   // teal-50  — relocate badge background

  // ─── DISPOSITION-CONSOLIDATE: rightsize cyan + prospect purple ───────────
  // dispRightsizeFg/Bg: cyan family for the Right-size disposition pill.
  // Brief spec: #06b6d4 (cyan-500). Distinct from T.teal (#0d8a9e, teal-700).
  // dispProspectFg/Bg: purple-500 family (#a855f7) for Prospect disposition.
  // Distinct from T.purple (#8b5cf6, violet-500) which is a lighter purple
  // used by the old Plan-S1 vocab; the brief specifies #a855f7 for Prospect.
  dispRightsizeFg: "#06b6d4",  // cyan-500  — Right-size disposition foreground
  dispRightsizeBg: "#ecfeff",  // cyan-50   — Right-size disposition badge tint
  dispProspectFg:  "#a855f7",  // purple-500 — Prospect disposition foreground
  dispProspectBg:  "#faf5ff",  // purple-50  — Prospect disposition badge tint

  // ── REPORTS-TILES-REWORK: per-report tint backgrounds ────────────────────
  //
  // cyanSoft is the soft tint for the People Portfolio tile (accent: T.statCyan,
  // sky-400). sky-50 matches the same low-saturation family as T.utilRed /
  // T.blueSoft / T.utilGreen / T.purpleBg used for the other tiles.
  // Do NOT consolidate with T.blueSoft (#e8f0fe) — that is a blue family tint
  // and reads distinctly from the sky-blue statCyan accent.
  cyanSoft: "#f0f9ff",   // sky-50 — tint for People Portfolio icon chip

  // ── SPACE-CAPACITY-ASSIGNED-BAR: Assigned bar fill ────────────────────────
  //
  // Muted gray fill for the Installed capacity baseline bar in LocationOverviewCard.
  // Matches --graybase from the reference comp. Recedes visually so Installed
  // reads as the quiet 100% reference, not a headline. Do NOT use T.blue here.
  barInstalled: "#c4cfdc",
  // Slate-blue fill for the Assigned vs Seats bar in LocationOverviewCard.
  // Distinct from T.mid (#64748b, attendance bar fill) and T.blue (#1a73e8,
  // capacity bar). Muted enough not to clash with the attendance gray or the
  // action blue, while reading clearly as a third distinct track.
  // Do NOT consolidate into T.mid or T.blue — all three bars need visual
  // separation so the user can parse Assigned / Attendance / Capacity at a
  // glance.
  barAssigned: "#5b6f99",

  // ── WHATS-AHEAD-PANE: hairline divider color ─────────────────────────────
  //
  // Ultra-light warm gray used as the between-item hairline rule in the
  // "What's Ahead" card (Direction C stacked sections). Same value as the
  // Space & Occupancy bar dividers in the reference design (--hair:#f1f4f7).
  // Distinct from T.border (#e2e8f0, cool blue-gray) — do not consolidate.
  hair: "#f1f4f7",

  // ── COMMS-OBJECT: subject-type badge colors ───────────────────────────────
  //
  // bg/fg pairs for comms subject-type chips in CommsLandingPage.
  // "Project" bg reuses T.bluePale (#dbeafe, blue-100).
  // "M&A" bg reuses T.promoteBg (#ede9fe, violet-100).
  // "Location" bg reuses T.warnYellowBg (#fef9c3, yellow-100).
  // "Company-wide" falls back to T.lightGray / T.mid.
  commProjectFg: "#1d4ed8",  // blue-700 — Project subject badge fg
  commMaFg:      "#7c3aed",  // violet-600 — M&A subject badge fg
  commMoveBg:    "#ccfbf1",  // teal-100 — Move subject badge bg
  commMoveFg:    "#0f766e",  // teal-700 — Move subject badge fg
  commLocFg:     "#a16207",  // yellow-700 — Location subject badge fg

  // ── Ask Portfolio panel chrome ────────────────────────────────────────────
  //
  // Scope chip bg/fg — the "scoped to <location>" chip in the Ask Popout header.
  // askScopeBg is a light periwinkle blue, distinct from T.blueSoft (#e8f0fe,
  // saturated) and T.blueXLight (#eff6ff, cool). Do NOT consolidate — each
  // surface has distinct hue/saturation characteristics by design.
  askScopeBg: "#eaf1fe",
  askScopeFg: "#1556a3",  // deep blue fg — pairs with askScopeBg

  // Input row border — the rounded text-input box border in the Ask Popout.
  // Slightly cooler and lighter than T.border (#e2e8f0). Do NOT consolidate —
  // the input chrome needs a softer border to avoid over-emphasizing the field.
  askInputBorder: "#dfe5ec",

  // Clear-scope button fg — the muted blue-gray ✕ clear button inside the
  // scope chip. Lighter than T.mid (#64748b) and more blue than T.light
  // (#94a3b8, slate-400). Pairs with askScopeBg chip background.
  askClearFg: "#7a93b5",

  // ── Hub chip palette ──────────────────────────────────────────────────────
  //
  // Background, border, and text for the "◆ Hub" chip rendered in
  // LocationTile, LocationsListTable, AmenityBulkSetPanel, PhotoPanel, and
  // PlanPage. hubChipBg/hubChipBorder are the pale blue-gray chip chrome;
  // hubChipText is the dark slate-navy label (#3b4a5e). hubSlate is a
  // slightly lighter slate-600 variant used exclusively in PlanPage — kept
  // separate to preserve the per-surface intent.
  // Do NOT consolidate hubChipText and hubSlate — they are different shades
  // used on different surfaces by design.
  hubChipBg:     "#eef1f5",  // pale blue-gray — hub chip background
  hubChipBorder: "#dde3ea",  // mid blue-gray  — hub chip border
  hubChipText:   "#3b4a5e",  // dark slate-navy — hub badge text (LocationTile, PhotoPanel, etc.)
  hubSlate:      "#475569",  // slate-600 — hub badge text in PlanPage

  // ── AI advisory tag palette ──────────────────────────────────────────────
  //
  // Background and foreground for the "✦ AI · advisory" chip in
  // Retained action-panel indigo tint.
  // Distinct from T.promoteBg (#ede9fe, violet-100) — different hue family.
  aiAdvisoryBg: "#eef2ff",  // indigo-50  — AI advisory tag background
  aiAdvisoryFg: "#4338ca",  // indigo-700 — AI advisory tag foreground

  // ── Reviews surface bucket chip + status pill palette (P-19) ─────────────
  //
  // One pair (bg + fg) per bucket chip, plus overdue status pill.
  // Reviewed / due pill colors reuse T.ragGreenBg/Text and T.ragAmberBg/Text.
  reviewLeaseChipBg:   "#eef2ff",  // indigo-50  — Lease action chip bg (same indigo family as aiAdvisoryBg)
  reviewLeaseChipFg:   "#2f5bd6",  // indigo-600 — Lease action chip text
  reviewInFocusChipBg: "#f3e8ff",  // purple-50  — In-focus chip bg
  reviewInFocusChipFg: "#7e22ce",  // purple-700 — In-focus chip text
  reviewPlanChipBg:    "#fdf4e3",  // amber-50   — Plan coverage chip bg (also reused by due status pill)
  reviewPlanChipFg:    "#b7791f",  // amber-700  — Plan coverage chip text (also reused by due status pill)
  reviewDataChipBg:    "#e6f4ff",  // sky-50     — Data health chip bg
  reviewDataChipFg:    "#0369a1",  // sky-700    — Data health chip text
  reviewOverdueBg:     "#fdece9",  // red-50     — Overdue status pill bg
  reviewOverdueFg:     "#c0362c",  // red-700    — Overdue status pill text

  // ── Signals feature severity / status palette ─────────────────────────────
  //
  // orange-600 — high-severity stripe in the board view, needs_triage status
  // pill fg, and triage inbox badge background. One step darker than
  // T.negotiateOrange (#f97316, orange-500) which is the Negotiate stage fg.
  // Do NOT consolidate — the Negotiate stage color and the high-severity signal
  // color are semantically distinct surfaces.
  orangeDark: "#ea580c",

  // gray-500 — low-severity kanban stripe. Cool gray from the Tailwind gray
  // family, distinct from T.mid (#64748b, slate-500) which is the standard
  // secondary-text color.  Do NOT consolidate with T.mid — different palette
  // family and different semantic role (severity stripe vs. text).
  gray: "#6b7280",

  // sky-700 — foreground text for the "Attached" status pill in the signals
  // register. Pairs with T.cyanSoft (#f0f9ff, sky-50) as the pill background.
  // Distinct from T.buildBlue (#0ea5e9, sky-500) which is the Build stage
  // badge dot/fill color.
  skyFg: "#0369a1",

  // ── EditLeaseModal: year-cost row edit highlight ───────────────────────────
  //
  // Very pale blue background applied to a year-cost table row while an
  // in-progress edit (ycEdits map entry) is present. Distinct from T.blueXLight
  // (#eff6ff, cool blue) and T.tint (#f5f8fc, general tint) — this shade is
  // slightly warmer and used exclusively for dirty-row state in YearCostsTable.
  // Do NOT consolidate with T.blueXLight or T.tint.
  ycEditBg: "#f0f7ff",

  // ── Soft-blue chip border ─────────────────────────────────────────────────
  //
  // Blue-300 border for chips that sit on a T.blueSoft (#e8f0fe) background,
  // e.g. the "Mutual" break-clause badge in LeaseSummaryTab. Distinct from
  // T.barTintBorder (#85b7eb, medium blue border for hero-bar tint strips) —
  // this is a lighter, chip-scale border. Do NOT consolidate with T.barTintBorder.
  blueSoftBorder: "#c5d5f8",

  // ── WOS-INTERACTION-S0: motion, easing, radius, and page-spacing tokens ──────
  //
  // These values live in T so that buildInteractionLayerCSS() has a single
  // token source and never carries raw dimension / function literals.
  // Motion durations are string CSS values; easing curves are CSS cubic-bezier()
  // function strings; radius values are px strings; spacing values use clamp()
  // for fluid scaling between viewport sizes.

  // Motion durations
  motDurationInstant: "0ms",
  motDurationFast:    "80ms",
  motDurationNormal:  "160ms",
  motDurationSlow:    "300ms",
  motDurationEnter:   "220ms",
  motDurationExit:    "160ms",

  // Easing curves (Material Design 3 motion tokens)
  easeStandard:   "cubic-bezier(0.4, 0, 0.2, 1)",
  easeDecelerate: "cubic-bezier(0, 0, 0.2, 1)",
  easeAccelerate: "cubic-bezier(0.4, 0, 1, 1)",
  easeSpring:     "cubic-bezier(0.34, 1.56, 0.64, 1)",

  // Border-radius scale
  radiusXs:   "4px",
  radiusSm:   "6px",
  radiusCard: "8px",
  radiusMd:   "10px",
  radiusLg:   "12px",
  radiusXl:   "16px",
  radiusFull: "9999px",

  // Fluid page-shell spacing — clamp(min, preferred, max).
  // Used by --space-page-x / --space-page-y in useInteractionLayer.js and
  // wired to <main> padding in App.jsx. Horizontal: 16–32 px over 480–1280 px;
  // vertical: 12–24 px over 480–900 px.
  spacePageX: "clamp(16px, 3vw, 32px)",
  spacePageY: "clamp(12px, 2vh, 24px)",

  // Toolbar layout tokens (TOOLBAR-CONTROL-HEIGHT-NORMALIZE-S1).
  // toolbarCtrlH: canonical height for every control in a page toolbar —
  //   Search, Filter, Sort, density, view-toggle, +Add buttons.
  //   Pinning height explicitly prevents browser line-height differences
  //   (e.g. different font-size × inherit line-height combos) from producing
  //   buttons that are 10–14 px taller than their siblings.
  // toolbarRowH: fixed height of the toolbar row container. Combined with
  //   overflow:hidden, this is the hard ceiling: no single control can
  //   stretch the row regardless of its own size.
  toolbarCtrlH: 33,
  alertChipSlotW: 50,
  toolbarRowH:  40,
  // toolbarSegH: height of each segment button inside the view toggle pill
  // (VIEW-TOGGLE-HEIGHT-UNIFY). Offices was 26, Leases was 24 — unified at 26.
  toolbarSegH:  26,

  // ── ActionsPanel urgency / action-type chip colors ────────────────────────
  //
  // actionAmberFg: amber-700 foreground for the "This Week" urgency label.
  // Pairs with T.utilAmber (#fffbeb) as the row background and T.ragAmberBg
  // (#fef3c7) as the chip tint. Distinct from T.amberDark (#854F0B) which is
  // used for lease-countdown pills — do NOT consolidate.
  actionAmberFg: "#b45309",

  // dismissPromptBg: near-white background for the inline dismiss / complete
  // prompt bars that expand beneath an action row. Distinct from T.hoverBg
  // (#f8fafc, cool blue-gray) — this is a warmer off-white. Do NOT merge.
  dismissPromptBg: "#fafafa",

  // actionTypeChipBg: pale indigo chip background for the Decision / Follow-up
  // type label in each action row. Pairs with T.blue (#1a73e8) as the label fg.
  actionTypeChipBg: "#f0f4ff",

  // ── Dev-fixture chrome (HeadingTooltipTestFixture) ────────────────────────
  //
  // These tokens cover the DEV-ONLY HeadingTooltipTestFixture component so the
  // L52 sweep can cover src/components/ without a special exclusion. The
  // fixture is never rendered in production (guarded by import.meta.env.DEV).
  navy:             "#1a1a2e",  // deep navy — fixture popup body text
  neutralBorder:    "#e0e0e0",  // cool neutral-gray — fixture popup chrome border
  textMuted:        "#555555",  // medium-dark muted — fixture instruction copy
  grayBorderXLight: "#dddddd",  // very light warm-gray — fixture panel dividers
  textDim:          "#666666",  // medium-gray — fixture table header label text

  // ── Market disposition / clarity palette (DISPOSITION_META / CLARITY_META) ─
  //
  // Foreground and background pairs for the DISPOSITION_META and CLARITY_META
  // objects in marketsClient.js. These are the original plan-module design
  // values, distinct from the DISPOSITION-CONSOLIDATE tokens (T.dispRightsizeFg
  // etc.) added for the newer Plan Direction-C UI.
  // DO NOT consolidate — different design generation, different surfaces.
  marketStayFg:        "#2f6f4f",  // dark forest-green fg — Stay/Renew + Clear clarity
  marketExpandFg:      "#1f6feb",  // blue fg — Expand disposition
  marketExpandBg:      "#e7f0ff",  // pale blue bg — Expand disposition badge
  marketConsolidateBg: "#f0e9fb",  // pale purple bg — Consolidate disposition badge
  marketRightsizeFg:   "#b06a00",  // amber-brown fg — Right-size + Forming clarity
  marketRightsizeBg:   "#fbf0df",  // pale amber bg — Right-size disposition badge
  marketExitFg:        "#9a3b3b",  // dark brick-red fg — Exit + Unclear clarity
  marketExitBg:        "#f7e7e7",  // pale rose bg — Exit disposition badge
  marketProspectFg:    "#5b6b7c",  // slate-blue fg — Prospect disposition

  // marketConsolidateStrategyFg/Bg: dedicated ochre pair for the market-level
  // Strategy "Consolidate" value (PLAN-ADD-CONSOLIDATE-STRATEGY-S1). Same
  // amber "reduce" family as marketRightsizeFg/Bg but a distinctly darker,
  // more saturated ochre so the two are separable at a glance. Do NOT reuse
  // T.marketConsolidateBg above — that pale purple pairs with T.purpleDeep
  // for the unrelated legacy office-disposition "Consolidate" badge.
  marketConsolidateStrategyFg: "#8a5a00",  // dark ochre fg — Consolidate strategy pill
  marketConsolidateStrategyBg: "#f5e6c8",  // warm tan bg — Consolidate strategy pill

  // ── Leadership Radar type-family color pairs ───────────────────────────────
  //
  // Six foreground+background pairs, one per Radar item type (decision, action,
  // issue, expiry, signal, project).  A single radarFlag petrol token is used
  // for the flat flag icon and flagged-row tint.
  //
  // Do NOT consolidate with existing semantic tokens (T.blue, T.green, etc.) —
  // the Radar palette is intentionally desaturated and distinct from the health/
  // urgency palette so the two systems don't visually collide.
  radarDecision:    "#1a73e8",  // blue fg — Decision type chip + icon
  radarDecisionBg:  "#e8f0fe",  // pale blue bg — Decision chip fill
  radarAction:      "#1f9d6b",  // forest-green fg — Action type chip + icon
  radarActionBg:    "#ecfdf5",  // pale mint bg — Action chip fill
  radarIssue:       "#c2102e",  // crimson fg — Issue type chip + icon + overdue text
  radarIssueBg:     "#fae8eb",  // pale rose bg — Issue chip fill
  radarExpiry:      "#c98a00",  // amber-ochre fg — Expiry type chip + near-due text
  radarExpiryBg:    "#fef3c7",  // pale amber bg — Expiry chip fill
  radarSignal:      "#7a4fc0",  // violet fg — Signal type chip + icon
  radarSignalBg:    "#f5f3ff",  // pale lavender bg — Signal chip fill
  radarProject:     "#4338ca",  // indigo fg — Project type chip + icon
  radarProjectBg:   "#eef2ff",  // pale indigo bg — Project chip fill
  radarFlag:        "#005f6b",  // petrol (dark teal) — flag icon fill + flagged row tint

  // ─── REPORTS-TILES-V2-HERO-NUMBER: fallback gradient for ReportTile banner ─
  // Used when tile.key is not found in REPORT_ACCENTS (unknown / future tiles).
  // Kept here (not in ReportTile.jsx) so no raw hex appears in .jsx files.
  reportBannerGradFallback: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
  reportCostLeakageGrad: "linear-gradient(135deg, #8e2420 0%, #c0392b 100%)",

  // ── UnderlineTabBar shared tab component tokens ───────────────────────────
  // Used by the treatment-A underline+descriptor tabs (Market Plan, Projects).
  // tabDescActive: muted blue-purple for the descriptor sub-label on the
  // active tab. Distinct from T.accent (#1a73e8) which is the label fg.
  // tabBadge*: badge pill inside tab label — two states (active / inactive).
  //   Active:   pale blue bg (#dfe9ff) + T.accent text — matches active tab tint.
  //   Inactive: cool near-white bg (#eef3f8) + slate-blue text (#5b6b7c).
  // Do NOT consolidate tabBadgeActiveBg with T.blueSoft (#e8f0fe) — different
  // shade; nor tabBadgeInactiveBg with T.policyBandBg (#eef3fb) — different hue.
  tabDescActive:      "#5a7fc4",  // active descriptor sub-label text (muted blue-purple)
  tabBadgeActiveBg:   "#dfe9ff",  // active tab badge background (pale blue)
  tabBadgeInactiveBg: "#eef3f8",  // inactive tab badge background (cool near-white)
  tabBadgeInactiveFg: "#5b6b7c",  // inactive tab badge foreground (slate-blue)

  // ── Lease Dates redesign — typed chip palette (5 variants) ───────────────
  // LEASE-DATES-LIST-ROW-CONFORM spec colors. Kept here so no raw hex appears
  // in LeaseDatesPane.jsx (L52 guard). Each pair is bg/fg for a chip variant.
  // Do NOT consolidate chipDecisionBg with T.purpleBg (#f5f3ff) — different hue.
  // Do NOT consolidate chipDecisionFg with T.purple (#8b5cf6) — darker violet family.
  // Do NOT consolidate chipExpiryBg with T.pillRedBg (#FCEBEB) — distinct shade.
  // Do NOT consolidate chipAbatementBg with T.ragAmberBg (#fef3c7) or
  //   T.pillAmberBg (#FAEEDA) — this is the comp-spec amber tint for this chip.
  // Do NOT consolidate chipCommenceBg with T.pillGreenBg (#EAF3DE) — different shade.
  // Escalation uses T.tabBadgeInactiveBg / T.tabBadgeInactiveFg (same values).
  // Expiry fg uses T.ragRed (#c2102e). Abatement fg uses T.ragAmberText (#a05c00).
  // Commence fg uses T.ragGreenText (#1a6f2e).
  chipDecisionBg:  "#efeaff",  // Decision chip background — light lavender
  chipDecisionFg:  "#6b3fb0",  // Decision chip foreground — mid violet
  chipExpiryBg:    "#fbe7ea",  // Expiry chip background   — pale rose
  chipAbatementBg: "#faf1dc",  // Abatement chip background — pale amber
  chipCommenceBg:  "#e2f4ec",  // Commence chip background  — pale mint

  // ── Lease Dates redesign — collapsed detail panel ────────────────────────
  // Text and cite colors inside the HistoryRow / UpcomingRow detail panels.
  // detailPanelText and detailPanelCite are only used in LeaseDatesPane.jsx.
  // Do NOT consolidate detailPanelText with T.mid (#64748b) — different hue/value.
  // Do NOT consolidate detailPanelCite with T.light (#94a3b8) — distinct slate family.
  detailPanelText: "#4a5c72",  // collapsed detail panel body text — dark slate
  detailPanelCite: "#95a4b6",  // collapsed detail panel citation  — muted blue-gray

  // ── Radar Signal & Noise admin panel (RadarSignalNoisePanel.jsx) ──────────
  // PillBadge status variants — "Trust-gated" and "Deferred" colors. These are
  // distinct from T.ragAmberBg/ragAmberText and T.ragRed/ragRedBg; they map to
  // a warmer amber-gold (gated) and earthy-peach (deferred) palette used only
  // in this admin panel.
  // Do NOT consolidate pillGatedBg with T.ragAmberBg (#fef3c7) — different shade.
  // Do NOT consolidate pillGatedFg with T.ragAmberText (#a05c00) — different amber.
  pillGatedBg:      "#fdf4e3",  // "Trust-gated" pill background — warm pale amber
  pillGatedFg:      "#b7791f",  // "Trust-gated" pill text       — mid amber-gold
  pillDeferredBg:   "#f2e2d2",  // "Deferred" pill background    — warm pale peach
  pillDeferredFg:   "#7a5c3e",  // "Deferred" pill text          — earthy brown

  // Toggle (on/off switch) locked-state colors. Used by the RadarSignalNoisePanel
  // Toggle sub-component for emitters that are trust-gated or deferred.
  toggleLockedBg:    "#e5e9f0",  // locked toggle track bg  — cool light gray
  toggleLockedThumb: "#f7f9fc",  // locked toggle thumb bg  — near-white cool gray

  // Live-preview banner foreground text on the dark-navy (#1E3A5F) background.
  // Distinct from T.heroSubBlue (#bcd0ec) — lighter/more muted blue-gray.
  // Banner background uses T.tooltip.bg (#1E3A5F).
  radarBannerFg: "#c8d6ea",  // preview banner sub-label text — light muted blue-gray

  // Project record drawer cover image — "Medium" faded wash.
  coverWashVertical: "linear-gradient(180deg, rgba(255,255,255,.62) 0%, rgba(255,255,255,.87) 50%, var(--cover-solid, #f7f9fc) 88%)",
  coverWashHorizontal: "linear-gradient(90deg, rgba(255,255,255,.85) 0%, rgba(255,255,255,0) 55%)",
  coverPanelBg: "rgba(255,255,255,.92)",
  coverPanelBlur: "blur(6px)",
  coverChipBg: "rgba(255,255,255,.88)",
  coverBadgeBg: "#1E3A5F",
  coverMinHeight: 190,
};

// Shared stacking order for the surfaces involved in shell navigation.
// Keep page content below KPI pop-outs, sticky chrome, modal overlays, and
// notifications; component-local layers should use these named values.
export const LAYERS = Object.freeze({
  page: 0,
  kpiPopover: 1050,
  stickyChrome: 1200,
  modal: 10000,
  toast: 11000,
});

// Stage metadata. Drives StageBadge label + color and the LocationCard
// left border. Keys must match the DB stage enum.
//
// LSE-VACATED — `vacated` slots between `prospecting` and `closed` in
// insertion order, mirroring the filter dropdown's logical grouping
// (Active → Pipeline → Inactive). AddLocationModal renders the stage
// dropdown via Object.keys(STAGES), so this position propagates to the
// edit form automatically.
// `vacated` uses dedicated tokens (T.vacatedBg / T.vacatedFg) — DO NOT
// consolidate back into T.amber / T.utilAmber for DRY reasons. Those
// tokens drive the health=yellow / "At Risk" badge and reusing them
// would make a Vacated stage pill and an At Risk health badge look
// identical when they appear in the same row.
//
// `dormant` — temporarily closed with reopening imminent (e.g. Middle East
// offices). Placed immediately after `operational` in the Active group.
// Uses T.red / T.redSoft to signal the inactive-but-returning state.
// DELIBERATE DIVERGENCE: Active-group placement reflects portfolio intent,
// not scoring eligibility. Dormant is NOT added to DEFAULT_EXCLUDED_STAGES
// (remains visible in All Active view) and NOT added to PIPELINE_STAGES.
// The scoring layer must check stage explicitly; Active group ≠ scorable.
export const STAGES = {
  draft:       { label: "Draft",       color: T.light,           bg: T.hair        },
  build:       { label: "In-Build",    color: T.buildBlue,       bg: T.buildBlueBg },
  operational: { label: "Operational", color: T.ragGreenText,    bg: T.ragGreenBg  },
  dormant:     { label: "Dormant",     color: T.red,             bg: T.redSoft     },
  vacated:     { label: "Vacated",     color: T.vacatedFg,       bg: T.vacatedBg   },
  closed:      { label: "Retired",     color: T.light,           bg: T.lightGray   },
};

// Health metadata. Drives HealthDot color/ring and the at-a-glance label.
export const HEALTH = {
  green:  { label: "On Track", color: T.greenDeep, bg: T.ragGreenBg, ring: T.statGreen  },
  yellow: { label: "At Risk",  color: T.ragAmber,  bg: T.ragAmberBg, ring: T.yellowRing },
  red:    { label: "Critical", color: T.ragRed,    bg: T.ragRedBg,   ring: T.redRing    },
};

export const REGIONS = ["AMER", "EMEA", "APAC"];

// Pre-signature Draft is pipeline scaffolding, never default active.
// Negotiating and prospecting are no longer valid office stages; they have been
// removed from the STAGES map and moved to DEFAULT_EXCLUDED_STAGES so any
// legacy DB rows holding either value are hidden from the default "All Active"
// view. Draft is the only current pipeline stage.
export const PIPELINE_STAGES = ["draft"];

// LSE-VACATED: Inactive stages — excluded from the default "All Active" view
// (stage="all") and only reachable via explicit filter selection. Exported here
// so that filterLocations.js and the Task-238 drift-guard test share a single
// source of truth; add new inactive stages to this array and the filter +
// test stay in sync automatically.
// OFFICE-STAGE-REMOVE-NEGOTIATING-PROSPECTING: negotiating and prospecting
// added so legacy DB rows are hidden from the default view until relocated.
export const DEFAULT_EXCLUDED_STAGES = ["draft", "closed", "vacated", "negotiating", "prospecting"];

// ─── PROJ-PR-1: Project module tokens ────────────────────────────────────────
//
// Drives ProjectsPage type pills, phase pills, and the HealthDot
// mapping for project status. Color families:
//   • new_build → blue, renovation → teal, critical_infra → purple
//   • exit / consolidation → red
//   • m_and_a → amber
//
// PROJECT_STATUS maps the project health vocabulary ('on_track'/'at_risk'/
// 'critical') to the same color contract as HEALTH so HealthDot can be
// driven via projectStatusToHealthKey() without a parallel component.

export const PROJECT_TYPE = {
  new_build:      { label: "New Build",       color: T.buildBlue,       bg: T.buildBlueBg },
  renovation:     { label: "Renovation",      color: T.teal,             bg: T.tealSoftBg },
  critical_infra: { label: "Critical Infra",  color: T.purple,           bg: T.purpleBg   },
  compliance:     { label: "Compliance",      color: T.buildBlue,       bg: T.buildBlueBg },
  exit:           { label: "Exit",            color: T.red,             bg: T.redSoft     },
  consolidation:  { label: "Consolidation",   color: T.red,             bg: T.redSoft     },
  m_and_a:        { label: "M&A Integration", color: T.amberDark,       bg: T.utilAmber   },
};

export const PROJECT_PHASE = {
  intake:        { label: "Intake",         color: T.mid,       bg: T.lightGray   },
  business_case: { label: "Business Case",  color: T.mid,       bg: T.lightGray   },
  planning:      { label: "Planning",       color: T.buildBlue, bg: T.buildBlueBg },
  design:        { label: "Design",         color: T.buildBlue, bg: T.buildBlueBg },
  procurement:   { label: "Procurement",    color: T.buildBlue, bg: T.buildBlueBg },
  build:         { label: "Build",          color: T.buildBlue, bg: T.buildBlueBg },
  move_launch:   { label: "Move / Launch",  color: T.buildBlue, bg: T.buildBlueBg },
  closeout:      { label: "Closeout",       color: T.mid,       bg: T.lightGray   },
  complete:      { label: "Complete",       color: T.green,     bg: T.greenSoft   },
};

// Maps project health vocabulary to the same color contract as HEALTH.
// Use projectStatusToHealthKey() to convert before passing to HealthDot.
export const PROJECT_STATUS = {
  on_track: { label: "On Track", color: T.greenDeep, bg: T.ragGreenBg, ring: T.statGreen  },
  at_risk:  { label: "At Risk",  color: T.ragAmber,  bg: T.ragAmberBg, ring: T.yellowRing },
  critical: { label: "Critical", color: T.ragRed,    bg: T.ragRedBg,   ring: T.redRing    },
};

// ─── PLAN-S1: Disposition + clarity module tokens ─────────────────────────
//
// Drives DispositionPill and PlanClarityDot in the Plan · Markets view.
// Each entry provides label, fg color (color), and bg badge color (bg).
// Values align with the DB CHECK constraint in 0099_markets.sql:
//   expand | maintain | rightsize | exit | relocate | prospect

export const DISPOSITION_META = {
  maintain:    { label: "Maintain",      color: T.ragGreenText,   bg: T.ragGreenBg     },
  expand:      { label: "Expand",        color: T.buildBlue,      bg: T.buildBlueBg    },
  rightsize:   { label: "Right-size",    color: T.amberDark,      bg: T.utilAmber      },
  exit:        { label: "Exit",          color: T.red,            bg: T.redSoft        },
  relocate:    { label: "Relocate",      color: T.dispRelocateFg, bg: T.dispRelocateBg },
};

// INVENTORY-CLEANUP-S1 audit: CLARITY_META is surfaced to users (PlanClarityDot.jsx)
// but there is no written definition anywhere of what "Clear" / "Forming" /
// "Unclear" mean or how a market's clarity value gets set. Flagging in place
// rather than removing since it's actively rendered — needs a definition
// before it can be trusted or safely cleaned up further.
export const CLARITY_META = {
  clear:   { label: "Clear",   color: T.green,    bg: T.greenSoft },
  forming: { label: "Forming", color: T.amberDark, bg: T.utilAmber },
  unclear: { label: "Unclear", color: T.red,       bg: T.redSoft  },
};

// ─── PULSE-LANDING-BUILD: 5-band score model colors ─────────────────────────
//
// One consistent model used across Portfolio Health ring and Location Health:
//   Strong 85–100 · Stable 70–84 · Watchlist 55–69 · At Risk 40–54 · Critical <40
//
// band key → foreground color (text / ring stroke)
// bandBg key → chip background paired with the foreground
// ─────────────────────────────────────────────────────────────────────────────
export const SCORE_BAND_FG = {
  strong:    "#1f9d6b",   // deep green
  stable:    "#1556a3",   // deep blue
  watchlist: "#9a6a00",   // dark amber
  at_risk:   "#a03a10",   // dark orange
  critical:  "#a51226",   // deep red
};
export const SCORE_BAND_BG = {
  strong:    "#e7f3ec",
  stable:    "#eaf1fe",
  watchlist: "#fbf0df",
  at_risk:   "#fae4d8",
  critical:  "#fae8eb",
};
// Matching panel border colors (softer than the foreground, pairs with SCORE_BAND_BG).
export const SCORE_BAND_BORDER = {
  strong:    "#cfe9dc",
  stable:    "#c2d9fb",
  watchlist: "#ecd9a8",
  at_risk:   "#f2c9b0",
  critical:  "#f5c0c8",
};
// Accent colors (for ring strokes, bar fills) — slightly brighter than fg.
export const SCORE_BAND_ACCENT = {
  strong:    "#1f9d6b",
  stable:    "#2f6feb",
  watchlist: "#d3a000",
  at_risk:   "#d9601a",
  critical:  "#c2102e",
};
export const SCORE_BAND_LABEL = {
  strong:    "Strong",
  stable:    "Stable",
  watchlist: "Watchlist",
  at_risk:   "At Risk",
  critical:  "Critical",
};

// ─── REPORTS-TILES-REWORK: per-report accent + tint pairs ────────────────────
//
// Keyed by REPORT_TILES tile key. `acc` is the accent color (icon stroke,
// "View report →" link, drag handle); `tint` is the low-saturation background
// for the icon chip. All values route through T — no raw hex here.
//
// Fallback for unknown keys: { acc: T.mid, tint: T.lightGray, grad: T.reportBannerGradFallback }.
// `grad` is a CSS linear-gradient string for the banner background.
export const REPORT_ACCENTS = {
  "report-lease-countdown":    { acc: T.red,          tint: T.utilRed,    grad: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)" },
  "report-office-capacity":    { acc: T.blue,         tint: T.blueSoft,   grad: "linear-gradient(135deg, #1a73e8 0%, #1558b0 100%)" },
  "report-office-utilization": { acc: T.green,        tint: T.utilGreen,  grad: "linear-gradient(135deg, #10b981 0%, #059669 100%)" },
  "report-wpr":                { acc: T.purple,       tint: T.purpleBg,   grad: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)" },
  "report-data-coverage":      { acc: T.aiAdvisoryFg, tint: T.aiAdvisoryBg, grad: "linear-gradient(135deg, #4338ca 0%, #3730a3 100%)" },
  "report-headcount-trend":    { acc: T.teal,         tint: T.tealSoftBg, grad: "linear-gradient(135deg, #0d8a9e 0%, #076880 100%)" },
  "report-open-roles":         { acc: T.teal,         tint: T.tealSoftBg, grad: "linear-gradient(135deg, #0d8a9e 0%, #076880 100%)" },
  "report-space-fit":          { acc: T.green,        tint: T.utilGreen,  grad: "linear-gradient(135deg, #059669 0%, #047857 100%)" },
  "report-rto-compliance":     { acc: T.blue,         tint: T.blueSoft,   grad: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)" },
  "report-foot-traffic":        { acc: T.teal,         tint: T.tealSoftBg, grad: "linear-gradient(135deg, #0f766e 0%, #115e59 100%)" },
  "report-utilization":         { acc: T.tealDarkFg,   tint: T.tealSoftBg, grad: "linear-gradient(135deg, #0a6b7a 0%, #0b5272 100%)" },
  "report-cost-leakage":        { acc: T.marketConsolidateStrategyFg, tint: T.marketConsolidateStrategyBg, grad: T.reportCostLeakageGrad },
};

// ─── LOCATION-INTEL-TAB-S1: tag dot colors for the Intel tab note log ────────
//
// Keyed by location_intel_notes.tag. Matches docs/location-intel-tab-comp.html
// dot colors (.d-seats / .d-lease / etc). Provenance/note-log only — never
// used by scoring.
export const INTEL_TAG_COLORS = {
  seats:       "#2563eb",
  lease:       "#7c3aed",
  headcount:   "#0d9488",
  cost:        "#d97706",
  utilization: "#0891b2",
  access:      "#64748b",
  market:      "#db2777",
  experience:  "#16a34a",
};
export const INTEL_TAG_LABELS = {
  seats:       "Seats",
  lease:       "Lease",
  headcount:   "Headcount",
  cost:        "Cost",
  utilization: "Utilization",
  access:      "Access",
  market:      "Market",
  experience:  "Experience",
};
export const INTEL_SOURCE_KIND_LABELS = {
  site_visit:  "Site visit",
  broker_call: "Broker call",
  email:       "Email",
  slack:       "Slack",
  it_ticket:   "IT ticket",
  facilities:  "Facilities",
  security:    "Security team",
  other:       "Other",
};
